import React from 'react';
import Proptypes from 'prop-types';
import _ from 'lodash';
import { SAMPLE_PLATE_LETTERS, SAMPLE_PLATE_NUMBERS } from '../../../constants/saxs';
import Plate from '../CrystalPlate/Plate';
import '../CrystalPlate/CrystalPlate.css';
import Experiment from '../../../helpers/Saxs/Experiment';

/** Local constants */
const ROUNDED_WELL = 'round';
const SQUARED_WELL = 'square';
const ROUNDED_SIZABLE_WELL = 'roundBlock';
const BUFFER_CONTAINING_WELL = 'bufferContainingWell';
const SAMPLE_CONTAINING_WELL = 'sampleContainingWell';

/**
 * React component which renders a set of plates
 */
class SaxsPlatesPanel extends React.Component {
    constructor(props) {
        super(props);
        this.getFilledWellsByPlate = this.getFilledWellsByPlate.bind(this);
        this.getDimensions = this.getDimensions.bind(this);
        this.getPlates = this.getPlates.bind(this);
        this.getPlateBySlotPosition = this.getPlateBySlotPosition.bind(this);
        this.getWellShape = this.getWellShape.bind(this);
        this.getWellSize = this.getWellSize.bind(this);
    }

    render() {
        if (!this.props.experiment) {
            return null;
        } else {
            return this.getPlates();
        }
    }

    /**
     * Get all the plates of the experiment to be rendered sorted by deck
     * @returns {array} plates
     */
    getPlates() {
        let result = [];

        let experiment = new Experiment(this.props.experiment);
        let plateGroups = experiment.getPlateGroups();

        for (let i = 0; i < plateGroups.length; i++) {
            let id = plateGroups[i].plateGroupId;
            let plates = experiment.getPlatesByPlateGroupId(id);
            let deckDimentions = this.getDimensions(plates);

            for (var j = 1; j <= deckDimentions.maxSlotPositionRow; j++) {
                for (var k = 1; k <= deckDimentions.maxSlotPositionColumn; k++) {
                    let plate = this.getPlateBySlotPosition(plates, j, k);
                    let rowTitle = plate.plateType && plate.plateType.rowCount > 0 ? SAMPLE_PLATE_LETTERS.slice(0, plate.plateType.rowCount) : null;
                    let columnTitle = plate.plateType && plate.plateType.columnCount > 0 ? SAMPLE_PLATE_NUMBERS.slice(0, plate.plateType.columnCount) : null;
                    if (rowTitle && columnTitle) {
                        result.push(<div
                            style={{ borderWidth: '1px', borderStyle: 'solid', marginBottom: '10px' }}
                        >
                            <div style={{ paddingLeft: '10px' }}> {plate && plate.plateType ? plate.plateType.name : null} </div>
                            <Plate plate={{
                                name: plate.name,
                                rowTitle: rowTitle,
                                colTitle: columnTitle,
                                wellShape: this.getWellShape(plate),
                                wellSize: this.getWellSize(plate),
                                title: 'no title',

                            }}
                                bufferTableRows={this.getFilledWellsByPlate(experiment.getSamples(), plate, BUFFER_CONTAINING_WELL)}
                                sampleTableRows={this.getFilledWellsByPlate(experiment.getSamples(), plate, SAMPLE_CONTAINING_WELL)}
                            />
                        </div>)
                    }
                }
            }
        }
        return result;
    }

    /**
     * Get the shape of the wells for a given plate
     * @param {*} plate the plate
     * @returns {String} one of 'round', 'square' or 'roundBlock' to fit our Plate component
     */
    getWellShape(plate) {
        if (plate && plate.plateType) {
            if (plate.plateType.name && plate.plateType.name === " 4 x ( 8 + 3 ) Block") {
                return ROUNDED_SIZABLE_WELL;
            }
            else if (plate.plateType.name && plate.plateType.name === "Deep Well") {
                return SQUARED_WELL;
            } else if (plate.plateType.name && plate.plateType.name === "96 Well plate") {
                return ROUNDED_WELL;
            }
        }
    }

    /**
     * Get the well size for a given plate
     * @param {*} plate the plate
     * @returns {Array} list of well size in percentage. Null if not avaialble
     */
    getWellSize(plate) {
        if (plate && plate.plateType && plate.plateType.name === " 4 x ( 8 + 3 ) Block") {
            return [25, 25, 25, 25, 25, 25, 25, 25, 80, 80, 80]
        };
        return null;
    }

    /**
     * Get filled well list for a given plate
     * @param {*} experimentSamples all samples of the experiment
     * @param {Object} samplePlate plate of interest
     * @return {array} a list of wells inn the cuurent format supported by the plate component. Example: [{plate: 'plateName', row: 'A', column: 2}]
     */
    getFilledWellsByPlate(experimentSamples, samplePlate, bufferOrSample) {
        let filledWells = [];
        if (experimentSamples && experimentSamples instanceof Array && experimentSamples.length > 0) {
            experimentSamples.forEach(experimentSample => {
                if (experimentSample.samplePlatePosition) {
                    if (experimentSample.samplePlatePosition.samplePlateId === samplePlate.samplePlateId) {
                        // this sample is part of the plate of interest
                        if ((!experimentSample.macromolecule && bufferOrSample === BUFFER_CONTAINING_WELL) || ((experimentSample.macromolecule && bufferOrSample === SAMPLE_CONTAINING_WELL))) {
                            filledWells.push({
                                plate: samplePlate.name,
                                row: SAMPLE_PLATE_LETTERS[experimentSample.samplePlatePosition.rowNumber - 1],
                                column: experimentSample.samplePlatePosition.columnNumber
                            })
                        }
                    }
                }
            });
        }
        return filledWells;
    }

    /**
     * Get Deck dimension for the plates of a given plate group
     * @return {Object} Number of plates in a row on the deck and number of plate in a column on the deck
     */
    getDimensions = function (plates) {
        let maxSlotPositionRow = 0;
        let maxSlotPositionColumn = 0;

        if (plates != null) {
            for (let i = 0; i < plates.length; i++) {
                /** Row **/
                let slotPositionRow = plates[i].slotPositionRow;
                if (slotPositionRow != null) {
                    slotPositionRow = parseFloat(slotPositionRow);
                    if (slotPositionRow > maxSlotPositionRow) {
                        maxSlotPositionRow = slotPositionRow;
                    }
                }
                /** Column **/
                let slotPositionColumn = plates[i].slotPositionColumn;
                if (slotPositionColumn != null) {
                    slotPositionColumn = parseFloat(slotPositionColumn);
                    if (slotPositionColumn > maxSlotPositionColumn) {
                        maxSlotPositionColumn = slotPositionColumn;
                    }
                }
            }
        }
        return {
            maxSlotPositionRow: parseFloat(maxSlotPositionRow),
            maxSlotPositionColumn: parseFloat(maxSlotPositionColumn)
        };
    };

    /** 
     * This returns a sample plate object based on the position it occupies on the deck 
     * @param {array} plates all plates of the deck
     * @param {row} row deck row
     * @param {col} column deck column
     * @returns {Object} plate found on the speficied deck position. Null when not found
     */
    getPlateBySlotPosition = function (plates, row, column) {
        if (plates && plates instanceof Array) {
            return _.find(plates, plate => plate.slotPositionRow == row && plate.slotPositionColumn == column) || null;
        }
        return null;
    };
}

export default SaxsPlatesPanel;

SaxsPlatesPanel.proptypes = {
    /* Experiment object*/
    experiment: Proptypes.object
}


// WEBPACK FOOTER //
// ./src/components/saxs/PlateSetup/SaxsPlatesPanel.js